/**
 * DemoTicketCard — the demo flow's booking summary + CTA.
 *
 * A deliberate fork of `BookingTicketCard`'s summary core rather than a mode
 * on it: the standard card's isFree/overage/needs-access ladder is exactly the
 * logic a demo must not inherit (a demo is free by definition and needs no
 * membership). What it keeps identical: the `TicketCard` composition, the
 * `useBooking()` sourcing, the not-enrolled block rendering (shared
 * `CLUB_NO_ACCESS_*` constants — the exact standard-flow behavior), and
 * `TicketCtaButton` for both CTA placements.
 *
 * CTA is explicit, not inferred: `mode: 'confirm'` (authed + enrolled →
 * "Confirm Demo Booking", immediate create) or `'continue'` (un-authed →
 * "Continue to Confirmation", advance to account creation).
 *
 * @module pages/demo/components/DemoTicketCard
 */

import { TicketCard, TicketDivider } from '@butter/ui/patterns';
import { BookingSummaryDisplay } from '@butter/ui/domains/booking';
import { TicketCtaButton } from '../../booking/shared/TicketCtaButton';
import { formatDuration } from '../../booking/shared/formatDuration';
import { useBooking } from '~/services/internalApi/booking';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { CLUB_NO_ACCESS_LABEL, CLUB_NO_ACCESS_MESSAGE } from '~/services/internalApi/club';
import { timeStringToMinutes, formatTime } from '@butter/library/datetime';
import { format, parseISO } from 'date-fns';

export interface DemoTicketCardProps {
  /** 'confirm' = authed + enrolled (create now); 'continue' = un-authed (advance to account creation). */
  mode: 'confirm' | 'continue';
  /** Authed but not enrolled at this club — disabled "No access" CTA + explanation, mirroring standard. */
  blocked?: boolean;
  onAction: () => void;
  canAct: boolean;
  isCreating: boolean;
  /**
   * Portal the mobile CTA to <body>. Only the mobile bay-substep instance may
   * set this — the desktop summary card must NOT, or its portaled button leaks
   * onto mobile and overlaps the "Select a Bay" button.
   */
  floatingMobileCta?: boolean;
  className?: string;
}

export function DemoTicketCard({
  mode,
  blocked = false,
  onAction,
  canAct,
  isCreating,
  floatingMobileCta = false,
  className = '',
}: DemoTicketCardProps) {
  const { selectedDate, selectedStartTime, selectedEndTime, selectedBayId, availableBays } =
    useBooking();
  const { data: club } = useClubQuery();

  const hasRequiredData = !!(
    selectedDate &&
    selectedStartTime &&
    selectedEndTime &&
    selectedBayId &&
    club
  );

  if (!hasRequiredData) {
    return (
      <TicketCard borderColor="var(--theme-100)" className={className}>
        <div className="p-6 opacity-50">
          <h2 className="mb-6 text-sm font-medium text-fg-primary">
            Demo Booking Summary
          </h2>
          <p className="text-sm text-fg-quaternary">
            Select a time and bay to see your demo summary
          </p>
        </div>
      </TicketCard>
    );
  }

  const selectedBay = availableBays?.items.find((b) => b.id === selectedBayId);
  const bayName = selectedBay?.name || `Bay #${selectedBayId}`;

  const startMinutes = timeStringToMinutes(selectedStartTime);
  const endMinutes = timeStringToMinutes(selectedEndTime);
  const durationMinutes = (endMinutes || 0) - (startMinutes || 0);

  const formattedDate = format(parseISO(selectedDate), 'EEEE, d MMMM, yyyy');
  const timeRange = `${formatTime(selectedStartTime)} - ${formatTime(selectedEndTime)}`;

  const label = blocked
    ? CLUB_NO_ACCESS_LABEL
    : mode === 'confirm'
      ? 'Confirm Demo Booking'
      : 'Continue to Confirmation';
  const ariaLabel = blocked
    ? CLUB_NO_ACCESS_LABEL
    : mode === 'confirm'
      ? 'Confirm demo booking'
      : 'Continue to confirmation';
  const disabled = isCreating || blocked || !canAct;
  const busyLabel = mode === 'confirm' ? 'Creating Booking...' : 'Processing...';

  return (
    <TicketCard
      borderColor="var(--theme-100)"
      punchRadius="1.3rem"
      className={className}
    >
      <BookingSummaryDisplay
        bayName={bayName}
        date={formattedDate}
        timeRange={timeRange}
        duration={formatDuration(durationMinutes)}
        guestCount={0}
      />

      <TicketDivider punch />

      {/* Bottom section — no pricing: a demo is free, and rendering "$0.00"
          would imply money changed hands (the Figma ticket hides its price
          block for the same reason). */}
      <div className="p-6" data-testid="demo-ticket-card-actions">
        {floatingMobileCta && (
          <TicketCtaButton
            variant="floating-mobile"
            dataTestId="demo-cta-button-floating"
            label={label}
            ariaLabel={ariaLabel}
            onClick={onAction}
            disabled={disabled}
            busy={isCreating}
            busyLabel={busyLabel}
          />
        )}

        <div
          className="hidden md:flex md:items-center md:justify-end md:gap-6"
          data-testid="demo-ticket-actions-desktop"
        >
          <TicketCtaButton
            variant="desktop"
            dataTestId="demo-cta-button"
            label={label}
            ariaLabel={ariaLabel}
            onClick={onAction}
            disabled={disabled}
            busy={isCreating}
            busyLabel={busyLabel}
          />
        </div>

        {/* Block explanation (not enrolled at this club) — same copy and
            rendering as the standard ticket card. */}
        {blocked && (
          <p
            className="mt-4 text-center text-sm text-fg-quaternary md:text-right"
            role="status"
            data-testid="booking-ticket-blocked-message"
          >
            {CLUB_NO_ACCESS_MESSAGE}
          </p>
        )}
      </div>
    </TicketCard>
  );
}
